import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Typed shape of `GET /api/v1/auth/whoami` (`WhoamiResponse` in
 * `Ami.Identity/Authentication/Endpoints/AuthEndpoints.cs`). The wire format is
 * camelCase (ASP.NET `JsonSerializerDefaults.Web`), so these interfaces mirror
 * the C# records field-for-field in camelCase.
 *
 * `whoami` is the SPA's source of truth for identity, effective roles, the
 * per-customer module-enablement flags, per-customer branding, and the active
 * impersonation session (if any). The shell composes its nav, guards, theme,
 * and impersonation banner from this single response.
 */
export interface WhoamiAuthenticated {
  /** The human's own user id (the signed-in Entra account). */
  userId: string;
  email: string;
  /** Authenticated roles, ordinal-sorted by the API. */
  roles: string[];
  isSiteAdministrator: boolean;
}

/**
 * The *effective* identity for the request. Equals {@link WhoamiAuthenticated}
 * when not impersonating; when impersonating, this is the impersonation target
 * and role/module guards evaluate against it (DDR-PLT-016).
 */
export interface WhoamiEffective {
  userId: string;
  /** Null for SiteAdministrator accounts that have no customer scope. */
  customerId: string | null;
  email: string;
  preferredLanguage: string;
  preferredTimeZone: string | null;
  /** Effective roles, ordinal-sorted by the API. */
  roles: string[];
  isAdministrator: boolean;
  isManager: boolean;
  isRequestDelegate: boolean;
  isSiteAdministrator: boolean;
}

/**
 * Per-customer branding. `theme.ts` rebuilds the palette from
 * `brandPrimaryColor` / `brandHeaderColor` and the sidebar shows `logoUrl`.
 */
export interface WhoamiCustomer {
  id: string;
  name: string;
  displayName: string | null;
  logoUrl: string | null;
  faviconUrl: string | null;
  brandPrimaryColor: string | null;
  brandHeaderColor: string | null;
  supportEmail: string;
  supportPhoneNumber: string | null;
}

/**
 * Module-enablement flags for the effective customer. Absent (`null`) only
 * when there is no effective customer scope (bare SiteAdmin); the API applies
 * the "row absent ⇒ both enabled" convention before returning, so when present
 * these are the literal flags. The sidebar hides disabled-module nav entirely.
 */
export interface WhoamiModuleSettings {
  isInsightsEnabled: boolean;
  isRequestsEnabled: boolean;
}

/** The user being impersonated. Mirrors `effective.userId` / `effective.customerId`. */
export interface WhoamiImpersonationTarget {
  userId: string;
  customerId: string | null;
  email: string;
  fullName: string;
}

/**
 * Present only when the request runs under an active impersonation session.
 * There is deliberately no expiry field — the banner shows elapsed time from
 * `startedAt`, never a countdown (block 020 / WEB PRD §5.7). The impersonating
 * admin is the top-level `authenticated` block; `target` is who they became.
 */
export interface WhoamiImpersonation {
  sessionId: string;
  target: WhoamiImpersonationTarget;
  startedAt: string;
  reason: string | null;
}

/** Top-level `whoami` response. */
export interface Whoami {
  isImpersonating: boolean;
  authenticated: WhoamiAuthenticated;
  effective: WhoamiEffective;
  customer: WhoamiCustomer | null;
  moduleSettings: WhoamiModuleSettings | null;
  impersonation: WhoamiImpersonation | null;
  /** Flattened permission strings, e.g. `identity.user.read`. */
  permissions: string[];
}

/** Stable query key for the whoami/user-context query. */
export const whoamiQueryKey = ['whoami'] as const;

/**
 * Restarts the SPA at the root because the identity the API answers as has
 * changed — starting or ending an impersonation.
 *
 * <p>
 * Everything the app holds belongs to the identity being left: the query cache,
 * the MUI theme built from that customer's branding, the resolved route tree,
 * and ordinary component state that no cache API reaches (the request form's
 * candidate registry, a half-filled filter bar). A reload discards all of it at
 * once, and the app boots as whoever the server now says we are.
 * </p>
 *
 * <p>
 * <b>Why not cache surgery.</b> Two attempts shipped and failed in production,
 * in opposite directions:
 * </p>
 * <ul>
 *   <li><b>BUG-045</b> — `clear()` then `invalidateQueries(['whoami'])` refetched
 *       <i>nothing</i>. `clear()` destroys each entry without re-pointing mounted
 *       observers, so `['whoami']` kept serving the identity being left, and the
 *       invalidate then searched a cache that had just been emptied. The shell
 *       never rebuilt until a manual reload.</li>
 *   <li><b>BUG-046</b> — `resetQueries()` refetched <i>everything</i>, including
 *       the queries of the route tree still on screen, which belongs to the
 *       identity being left. Starting an impersonation from the SiteAdmin console
 *       refetched `/site/*` as the impersonated customer user and took a 403 on
 *       each; ending one refetched the customer screens as a SiteAdministrator,
 *       who has no customer scope.</li>
 * </ul>
 *
 * <p>
 * Those are two halves of one dilemma rather than two independent mistakes.
 * Between the identity flipping and the router landing on the new home, the
 * outgoing tree is mounted under the incoming identity and every query it owns is
 * now unauthorized: refetch them and they 403, leave them and the shell never
 * rebuilds. Reordering only moves the window — refreshing `whoami` first exposes
 * the old tree to the new identity, navigating first resolves the route from a
 * stale one. A reload is the one option in which the two identities never
 * coexist.
 * </p>
 *
 * <p>
 * The cost is a page load on a deliberate, infrequent action — and it is what an
 * operator was doing by hand anyway. MSAL caches tokens in `sessionStorage`
 * (`auth/msalConfig`), so a same-tab reload keeps the session and does not
 * re-prompt.
 * </p>
 *
 * <p>
 * `replace` rather than `assign`, so the route the other identity was viewing is
 * not one Back away; `/` re-resolves per identity, landing a SiteAdministrator on
 * the console and a customer user on their dashboard.
 * </p>
 */
export function restartForIdentityChange(): void {
  window.location.replace('/');
}

async function fetchWhoami(signal?: AbortSignal): Promise<Whoami> {
  // Path is relative to VITE_API_BASE_URL, which already includes `/api/v1`.
  const { data } = await apiClient.get<Whoami>('/auth/whoami', { signal });
  return data;
}

export interface UseWhoamiOptions {
  /**
   * Set false to read the cached value without triggering a fetch. Callers that
   * render OUTSIDE the `RequireAuth` guard (e.g. `BrandedThemeProvider`, which
   * wraps the router and therefore also the public magic-link routes) must gate
   * on MSAL's `useIsAuthenticated()` — an unauthenticated `whoami` would 401 and
   * the client has no bearer token to send. Defaults to true.
   */
  enabled?: boolean;
}

/**
 * The user-context query. Identity is session-stable, so `staleTime` is
 * infinite — the value only changes on impersonation start/end and language
 * change, which invalidate `['whoami']` explicitly rather than relying on
 * time-based refetch.
 *
 * The query key is shared, so multiple callers (the shell, the TopBar, the
 * theme provider) de-duplicate onto a single request.
 */
export function useWhoami({ enabled = true }: UseWhoamiOptions = {}) {
  return useQuery({
    queryKey: whoamiQueryKey,
    queryFn: ({ signal }) => fetchWhoami(signal),
    staleTime: Number.POSITIVE_INFINITY,
    enabled,
  });
}

/**
 * Convenience selector for the effective role set — the basis for role-gated
 * nav and `RequireRole`. Returns an empty array until the query resolves.
 */
export function useEffectiveRoles(): string[] {
  const { data } = useWhoami();
  return data?.effective.roles ?? [];
}

/**
 * True when the current identity holds `permission`.
 *
 * Returns `false` until `whoami` resolves, so a gated control appears once the answer is
 * known rather than flashing in and being withdrawn. Read the name from
 * `auth/permissions.ts` rather than passing a literal.
 *
 * <p>
 * Prefer the role flags on {@link WhoamiEffective} for the ordinary case — they are what
 * the rest of the shell gates on. Reach for this when the capability does not follow the
 * effective role, which today means a SiteAdministrator action exercised inside an
 * impersonation session: the effective identity is then the customer user, so
 * `effective.isSiteAdministrator` is false even though the API will allow the call
 * (AU-62).
 * </p>
 */
export function useHasPermission(permission: string): boolean {
  const { data } = useWhoami();
  return data?.permissions.includes(permission) ?? false;
}

/**
 * Convenience selector for the module-enablement flags used to gate module
 * nav / `RequireModule`. Returns `null` until the query resolves or when the
 * effective identity has no customer scope.
 */
export function useModuleSettings(): WhoamiModuleSettings | null {
  const { data } = useWhoami();
  return data?.moduleSettings ?? null;
}
